import { revokeSession } from '@mygpt/app/apis';
import { KEY_MYGPT_TOKEN as GET_KEY_MYGPT_TOKEN } from '@mygpt/app/apis/constant';
import Cookies from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';
import { deleteTokenInfo, KEY_MYGPT_PATH_NAME, refreshToken as refreshTokenRequest } from '@mygpt/app';
import { KEY_MYGPT_REFRESH_TOKEN, KEY_MYGPT_TOKEN_EXPIRESIN } from '@mygpt/app';
import { TokenInfo } from '@/types/interface';
import { saveTokenInfo } from '@/utils/tool';

const getLocalLogin = () => {
  return window.location.origin + '/auth/login';
};

export const useLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const logout = async () => {
    const isLocalMode = import.meta.env.VITE_MODE === 'local';

    await revokeSession(Cookies.get(GET_KEY_MYGPT_TOKEN())).catch(() => undefined);
    deleteTokenInfo();

    if (isLocalMode) {
      // 本地部署版：直接跳转到本地登录页面
      console.log('[useLogin] Local mode - redirecting to /auth/login');
      window.location.href = '/auth/login';
    } else {
      // 线上版本：调用后端登出接口
      console.log('[useLogin] Online mode - calling backend logout API');
      // 登出时清理保存的路径
      localStorage.removeItem(KEY_MYGPT_PATH_NAME);

      // redirect_uri： 由后端环境控制是否使用该参数跳到本地登录页
      const APIBaseURL = window.ENV.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
      window.location.href = APIBaseURL + `/logout?redirect_uri=${encodeURI(getLocalLogin())}`;
    }
  };

  const login = (path?: string) => {
    const isLocalMode = import.meta.env.VITE_MODE === 'local';

    savePath(path);

    if (isLocalMode) {
      // 本地部署版：直接跳转到本地登录页面
      console.log('[useLogin] Local mode - redirecting to /auth/login');
      window.location.href = '/auth/login';
    } else {
      // 线上版本：跳转到登录页
      window.location.href = '/auth/login';
    }
  };

  const signup = (path?: string) => {
    savePath(path);
    const APIBaseURL = window.ENV.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
    window.location.href = APIBaseURL + '/login?screen_hint=signup';
  };

  const savePath = (path?: string) => {
    if (!path) return;
    const pathName = path ? path : location.pathname;
    // 记录path，登录回来后自动跳到该地址
    // 使用localStorage支持跨窗口访问，考虑到注册后，需要确认邮件点击打开新窗口登录
    localStorage.setItem(KEY_MYGPT_PATH_NAME, pathName);
  };

  const loginCallback = () => {
    const pathname = localStorage.getItem(KEY_MYGPT_PATH_NAME);
    if (pathname && pathname !== location.pathname) {
      navigate(pathname);
    } else {
      navigate('/');
    }
    localStorage.removeItem(KEY_MYGPT_PATH_NAME);
  };

  /**
   * 更新Token信息（公用方法）
   * 接受TokenInfo数据，保存并安排自动刷新
   * 可用于登录回调、接受邀请等任何返回新token的场景
   */
  const updateToken = (data: TokenInfo) => {
    saveTokenInfo(data);
    scheduleTokenRenewal();
  };

  // 刷新token
  const refreshToken = async () => {
    const refresh_token = Cookies.get(KEY_MYGPT_REFRESH_TOKEN());
    if (!refresh_token) return;
    const res = await refreshTokenRequest({ refresh_token });
    if (!res.data) return;
    const data: TokenInfo = res.data;
    saveTokenInfo(data);
    scheduleTokenRenewal();
    return data;
  };
  const scheduleTokenRenewal = () => {
    const expiresIn = Cookies.get(KEY_MYGPT_TOKEN_EXPIRESIN()) || 0;
    if (!expiresIn) return;

    const now = new Date().getTime();
    const delay = Number(expiresIn) - now - 60000;

    if (delay <= 0) return;

    setTimeout(() => {
      refreshToken();
    }, delay);
  };

  return {
    login,
    logout,
    signup,
    loginCallback,
    savePath,
    updateToken,
    refreshToken,
    scheduleTokenRenewal,
  };
};
